import React, { useContext, useEffect, useRef, useState } from "react";
import { Modal } from "antd";
import { ICelebrationModalProps, IWish } from "../types/celebration-card.types";
import EmployeeAvatar from "../../../employee/avatar/employee-avatar";
import { getNameObject } from "../../../../utility/event.utils";
import EmptyData from "../../../common/empty-data/empty-data";
import { Calendar1, Gift, Pencil, SmilePlus, Trash2 } from "lucide-react";
import TextArea from "antd/lib/input/TextArea";
import server from "../../../../server";
import UserLoginContext from "../../../../context/userLoginContext";
import LoadingSkeleton from "../../../common/loading-skeleton/loading-skeleton";
import dayjs from "dayjs";
import Locale from "../../../../core/locale/locale";
import EmojiPicker, { EmojiStyle } from "emoji-picker-react";
import "../../../../styles/component/send-wishes-modal.scss";

const SendWishesModal = (props: ICelebrationModalProps) => {
    const {user} = useContext(UserLoginContext);

    const [wish, setWish] = useState("");
    const [editingWishId, setEditingWishId] = useState<string | null>(null);
    const [updatedWish, setUpdatedWish] = useState("");
    const [openEmojiPicker, setOpenEmojiPicker] = useState<boolean>(false);
    const textAreaRef = useRef<any>(null);
    const selectionRef = useRef({start: 0, end: 0});
    const emojiPickerRef = useRef<HTMLDivElement | null>(null);
    const shouldHideOk = !props.canSendWish;


    useEffect(() => {

        function handleClickOutside(event: any) {
            if (emojiPickerRef.current &&
                !emojiPickerRef.current.contains(event.target)) {
                setOpenEmojiPicker(false);
            }
        }

        document.addEventListener('mousedown', handleClickOutside);

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };

    }, [emojiPickerRef])

    const handleModalClose = () => {
        props.setOpen(false);
        setWish("");
        setUpdatedWish("");
        setEditingWishId("");
    }

    async function handleSendWishes(eventId: string) {
        const payload = {
            message: wish
        }
        try {
            await server.wish.create(eventId, payload);
            setWish("")
        } catch (e) {
            console.log("Error while sending wish", e);
        }
    }

    async function handleUpdateWish(wishId: string) {
        const payload = {
            message: updatedWish
        };
        try {
            await server.wish.update(wishId, payload);
            setEditingWishId(null);
            setUpdatedWish("");
        } catch (e) {
            console.error("Error while updating wish", e);
        }
    }

    async function handleDeleteWish(wishId: string) {
        try {
            await server.wish.delete(wishId);
        } catch (e) {
            console.error("Error while deleting wish", e);
        }
    }


    const updateSelection = (e: React.SyntheticEvent<HTMLTextAreaElement>) => {
        selectionRef.current = {
            start: e.currentTarget.selectionStart,
            end: e.currentTarget.selectionEnd,
        };
    };

    function insertEmoji(emojiObj: any) {
        const textarea = textAreaRef.current?.resizableTextArea?.textArea;
        if (!textarea) return;

        const start = textarea.selectionStart;
        const end = textarea.selectionEnd;

        setWish(prevWish => {
            const newText =
                prevWish.slice(0, start) +
                emojiObj.emoji +
                prevWish.slice(end);

            requestAnimationFrame(() => {
                textarea.focus();
                const pos = start + emojiObj.emoji.length;
                textarea.setSelectionRange(pos, pos);
            });

            return newText;
        });
    }

    return (
        <Modal
            className={'hrms-app wishes-modal'}
            open={props.open}
            onCancel={handleModalClose}
            okText={"Send"}
            onOk={() => handleSendWishes(props?.event?.id ?? "")}
            okButtonProps={shouldHideOk ? {style: {display: 'none'}} : {}}
            afterOpenChange={(open) => {
                if (open) {
                    const container = document.getElementById("wish-list-container");
                    if (container) {
                        container.scrollTop = container.scrollHeight;
                    }
                }
            }}
            title={
                <div className={'wish-modal-header'}>
                    <div className={'employee-avatar'}>
                        <EmployeeAvatar
                            size={50}
                            name={getNameObject(props.event?.name ?? "")}
                            thumbnailId={props.event?.profilePhoto?.thumbnails?.small?.id}/>
                    </div>
                    <div className={'dashboard-celebration-modal-title'}>
                        <div className={'employee-name'}>
                            {props.event?.name}
                        </div>
                        <div className={'employee-designation'}>
                            {props.event?.designation}
                        </div>
                        <div>
                            <div className={`date-and-icon ${props.isToday ? "today" : "upcoming"}`}>
                                <div>
                                    {props.isToday ? <Gift/> : <Calendar1/>}
                                </div>
                                <div>
                                    {props.isToday ? "Today," : "Upcoming,"} {props.event?.startDate ? dayjs(props.event.startDate).format(Locale.displayDateAndMonth) : ""}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            }
        >
            {
                <LoadingSkeleton loading={props.loadingWishes}>
                    <div className={'wishes-modal'}>
                        {
                            props.wishes.length > 0 ?
                                <div className={'wish-list-container'} id={"wish-list-container"}>
                                    {props.wishes.map((item: IWish) => {
                                        const selfMessages = item.senderUserId === user.id;

                                        return (
                                            <div className={`wish-profile-and-name ${selfMessages ? 'self-text' : ''}`}
                                                 key={item.id}>
                                                <div>
                                                    <EmployeeAvatar
                                                        name={getNameObject(item.senderName)}
                                                        thumbnailId={item.senderProfilePhotoThumbnailId}
                                                    />
                                                </div>
                                                <div className={'wish-list'} key={item.id}>
                                                    <div className={'name-message'}>
                                                        <div className={'name-time'}>
                                                            <div className={'name'}>
                                                                {item.senderName}
                                                            </div>
                                                            <div className={'date-and-time'}>
                                                                <div>
                                                                    {item.sentAt ? `${dayjs(item.sentAt).format(Locale.displayDateAndMonth)},` : ""}
                                                                </div>
                                                                <div>
                                                                    {item.sentAt ? dayjs(item.sentAt).format(Locale.displayTimeShort) : ""}
                                                                </div>
                                                            </div>
                                                        </div>
                                                        <div className={'message-and-actions'}>
                                                            {
                                                                editingWishId === item.id ? (
                                                                    <div className="edit-wish-container">
                                                                        <TextArea
                                                                            value={updatedWish}
                                                                            onChange={(e) => setUpdatedWish(e.target.value)}
                                                                        />

                                                                        <div className="edit-actions">
                                                                            <button
                                                                                onClick={() => handleUpdateWish(item.id)}
                                                                            >
                                                                                Save
                                                                            </button>

                                                                            <button
                                                                                onClick={() => {
                                                                                    setEditingWishId(null);
                                                                                    setUpdatedWish("");
                                                                                }}
                                                                            >
                                                                                Cancel
                                                                            </button>
                                                                        </div>
                                                                    </div>
                                                                ) : (
                                                                    <div className={'wish-message'}>{item.message}</div>
                                                                )
                                                            }
                                                            {selfMessages &&
                                                            <div className={'wish-actions'}>
                                                                <Pencil
                                                                    onClick={() => {
                                                                        setEditingWishId(item.id);
                                                                        setUpdatedWish(item.message);
                                                                    }}
                                                                />
                                                                <Trash2
                                                                    onClick={() => handleDeleteWish(item.id)}
                                                                />
                                                            </div>
                                                            }
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        )
                                    })}
                                </div>
                                :
                                <EmptyData
                                    icon={<Gift className={'empty-wishes-data-icon'}/>}
                                    title={props.activeTimeFilter === "passed" ? "No Wishes Received!" : "No Wishes Yet!"}
                                    subtitle={props.activeTimeFilter === "passed" ? " No wishes were shared for this celebration" : `Be the first to send your wishes and make
                                    ${props?.event?.name}'s day special.`}
                                />
                        }
                    </div>
                    {
                        props.canSendWish && <div className={'wish-message-box'}>
                            <TextArea
                                ref={textAreaRef}
                                className={'wish-message-box__content-box'}
                                value={wish}
                                onChange={(e) => setWish(e.target.value)}
                                placeholder={
                                    props.event?.employeeId === user.employeeId
                                        ? "Reply..."
                                        : "Write a warm wish..."
                                }
                                onClick={updateSelection}
                                onKeyUp={updateSelection}
                                onSelect={updateSelection}
                            />
                            <SmilePlus className={`wish-message-box__icon ${openEmojiPicker ? 'active' : 'inactive'}`}
                                       onClick={() => setOpenEmojiPicker(true)}/>

                            {openEmojiPicker && (
                                <div className="wish-message-box__emoji-picker" ref={emojiPickerRef}>
                                    <EmojiPicker
                                        emojiStyle={EmojiStyle.GOOGLE}
                                        width={'100%'}
                                        height={300}
                                        previewConfig={{
                                            showPreview: false,
                                        }}
                                        onEmojiClick={insertEmoji}
                                    />
                                </div>
                            )}
                        </div>
                    }

                </LoadingSkeleton>
            }
        </Modal>
    )
}

export default SendWishesModal;
